import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Compone classi Tailwind risolvendo i conflitti a favore dell'ultima. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Genera una chiave di idempotenza per le operazioni critiche.
 * Il valore è stabile finché l'utente non avvia una nuova operazione: è ciò che
 * rende sicuro un retry dopo un errore di rete.
 */
export function newIdempotencyKey(scope: string): string {
  return `${scope}-${crypto.randomUUID()}`;
}

/** Iniziali per gli avatar testuali. */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** Ritarda l'esecuzione, usata per il debounce delle ricerche. */
export function debounce<A extends unknown[]>(fn: (...args: A) => void, delay: number) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (...args: A) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

/** Converte un elenco di record in CSV, con escaping conforme a RFC 4180. */
export function toCsv(rows: ReadonlyArray<Record<string, unknown>>, headers?: readonly string[]): string {
  if (rows.length === 0) return '';
  const columns = headers ?? Object.keys(rows[0] ?? {});
  const escape = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    const text = String(value);
    return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  // Il punto e virgola è il separatore atteso da Excel con impostazioni italiane.
  const lines = [columns.join(';'), ...rows.map((row) => columns.map((c) => escape(row[c])).join(';'))];
  return lines.join('\r\n');
}

/** Avvia il download di un file generato lato client. */
export function downloadFile(filename: string, content: string, mimeType = 'text/csv;charset=utf-8'): void {
  // Il BOM (U+FEFF) fa sì che Excel riconosca la codifica UTF-8 e mostri
  // correttamente gli accenti. È scritto come escape per non introdurre un
  // carattere invisibile nel sorgente.
  const bom = '\uFEFF';
  const blob = new Blob([content.startsWith(bom) ? content : bom + content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
